<template>
  <div class="subscription-card" role="button" tabindex="0" @click="emit('click')">
    <div class="subscription-header">
      <div class="subscription-header-left">
        <div class="subscription-avatar">
          <v-avatar v-if="item.vendorLogo" size="40">
            <v-img
              :src="item.vendorLogo"
              :alt="item.vendorName || item.packageName || 'Vendor avatar'"
              cover
            />
          </v-avatar>
          <span v-else class="subscription-avatar-text">
            {{ vendorInitials(item.vendorName || item.packageName || "SU") }}
          </span>
        </div>

        <div class="subscription-header-copy">
          <p class="subscription-name">{{ item.packageName || "Subscription" }}</p>
          <p class="subscription-meta-line">{{ item.vendorName || "Unknown vendor" }}</p>
        </div>
      </div>

      <div class="subscription-status">
        <span class="status-dot" :class="`status-dot--${item.status.toLowerCase()}`" />
        <span class="status-text">{{ item.status }}</span>
      </div>
    </div>

    <div class="subscription-body">
      <div class="subscription-details">
        <div class="subscription-details-left">
          <div class="subscription-detail-row">
            <v-icon size="16" class="subscription-detail-icon">mdi-currency-usd</v-icon>
            <span>
              {{ item.planAmount != null ? formatCurrency(item.planAmount) : "-" }} /{{ item.billingInterval || "-" }}
            </span>
          </div>
          <div class="subscription-detail-row">
            <v-icon size="16" class="subscription-detail-icon">mdi-calendar-sync-outline</v-icon>
            <span>
              Next Billing :
              <span class="text-success">{{ item.nextBillingDate ? formatDate(item.nextBillingDate) : "-" }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AppCard from "@/shared/components/ui/AppCard.vue";
import { formatCurrency, formatDate, getStatusColor, getTextColor } from "@/shared/utils/utils";

type SubscriptionStatus = "Active" | "Paused" | "Inactive" | "All";

interface SubscriptionItem {
  id: string | number;
  packageName: string;
  planAmount: number | null;
  billingInterval: string;
  nextBillingDate: string | null;
  status: SubscriptionStatus;
  vendorName: string;
  vendorLogo: string;
}

defineProps<{
  item: SubscriptionItem;
}>();

const emit = defineEmits<{
  (event: "click"): void;
}>();

function vendorInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}
</script>

<style scoped>
.subscription-card {
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 100%;
  cursor: pointer;
  /* border: 1px solid #e5e7eb !important; */
  border-radius: 18px;
  overflow: hidden;
  padding: 0;
  background-color: #fff !important;
}

.subscription-card:focus,
.subscription-card:focus-visible,
.subscription-card:active {
  box-shadow: none !important;
  transform: none !important;
  background-color: #fff !important;
}

.subscription-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  align-items: center;
}

.subscription-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.subscription-avatar {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background-color: #e1e1e1;
  overflow: hidden;
}

.subscription-avatar-text {
  font-size: 12px;
  font-weight: 800;
  color: #1f2937;
  letter-spacing: 0.04em;
}

.subscription-header-copy {
  min-width: 0;
}

.subscription-name {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  line-height: 1.25;
}

.subscription-meta-line {
  margin: 2px 0 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.35;
  text-transform: capitalize;
}

.subscription-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-self: end;
  white-space: nowrap;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  display: inline-block;
  background: #9ca3af;
}

.status-dot--active {
  background: #36923b;
}

.status-dot--paused {
  background: #f57c00;
}

.status-dot--inactive {
  background: #757575;
}

.status-text {
  font-size: 14px;
  font-weight: 500;
  color: #52606d;
  text-transform: capitalize;
}

.subscription-body {
  padding: 14px 16px 16px;
  display: grid;
  gap: 12px;
}

.subscription-details {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
}

.subscription-details-left {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.subscription-detail-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  color: #475569;
  line-height: 1.45;
}

.subscription-detail-icon {
  color: #94a3b8;
  margin-top: 1px;
  flex-shrink: 0;
}
</style>
